/* ============================================================================
   AuthProvider, persistent session state for the cross-device account system.
   ----------------------------------------------------------------------------
   Stores the bearer token in localStorage when available so the user stays
   signed in across refreshes and tabs. Falls back to in-memory state if
   storage is blocked (some sandboxed iframe contexts).
   ============================================================================ */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { readFirstTouch } from "@/lib/acquisition";
import { setAuthToken, queryClient } from "@/lib/queryClient";
import { API_BASE } from "@/lib/apiBase";
import { setSentryAccount } from "@/lib/sentry";
import { requestProfileSetup } from "@/lib/profileSetupRequest";
import { isTrainerProfileSet } from "@/lib/trainerSetup";

// ── Persistent token storage ───────────────────────────────────────────────────
//
// Moved to @/lib/tokenStorage: the Keychain / Keystore on native, localStorage
// on web. That module owns the platform split and the one-time migration of
// existing localStorage tokens.
//
// The consequence here is that reading the token is now ASYNCHRONOUS on native,
// because Keychain access is. The bootstrap below is split accordingly:
//
//   web    — unchanged. Still hydrates synchronously from localStorage in the
//            useState initializer, so web behaviour is byte-for-byte what it
//            was. Keeping this path identical is deliberate; it is the platform
//            the change does not benefit, so it should not carry any risk.
//   native — boots with a null token and isRestoring=true, then resolves the
//            token from the Keychain in the restore effect. isRestoring MUST
//            start true there or the sign-in modal flashes over a session that
//            is about to restore.
import { loadToken, persistToken } from "@/lib/tokenStorage";
import { Capacitor } from "@capacitor/core";

function nativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Synchronous web-only hydrate. Always null on native — see above. */
function readStoredTokenSyncWebOnly(): string | null {
  if (nativePlatform()) return null;
  try {
    const v = window.localStorage.getItem("nexus_dex_session_token");
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Build an absolute URL for an asset served by the backend (e.g. /uploads/...).
 * In dev API_BASE is empty so this just returns the path; in deployed builds it
 * prefixes the port-5000 proxy so static uploads remain reachable.
 */
export function backendAssetUrl(pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("data:")) return pathOrUrl;
  if (!pathOrUrl.startsWith("/")) return pathOrUrl;
  return `${API_BASE}${pathOrUrl}`;
}

export interface PortalAccount {
  id: number;
  email: string;
  displayName: string | null;
  trainerCode: string | null;
  homeRegion: string | null;
  isAdmin?: boolean;
  createdAt: string | number;
  lastSeenAt: string | number | null;
  // Auth-expansion fields
  username?: string | null;
  trainerName?: string | null;
  friendCode?: string | null;
  /**
   * Legacy. Builds before 2026-10-02 uploaded a profile screenshot; nothing in
   * the app reads this any more (lib/trainerSetup has the profile rule).
   */
  profileScreenshotUrl?: string | null;
  // Pokemon GO trainer level (1..80). Typed with the trainer name at sign-up
  // and changed from Settings. Null until first set.
  trainerLevel?: number | null;
  /**
   * The trainer typed this name and level themselves. `false` on an account
   * that has not (it is asked when the app opens, lib/trainerSetup); absent on
   * a payload from before the server knew, which is read as "not known".
   */
  trainerConfirmed?: boolean;
  emailVerified?: boolean;
  tier?: 'free' | 'pro';
  /** The "How did you find Nexus Dex?" answer is already on the account. */
  heardAboutAnswered?: boolean;
}

interface AuthState {
  token: string | null;
  account: PortalAccount | null;
  isSignedIn: boolean;
  /** True while we validate a persisted token against the backend on first load. */
  isRestoring: boolean;
  isAuthOpen: boolean;
  authMode: "signin" | "signup";
  openAuth: (mode?: "signin" | "signup") => void;
  closeAuth: () => void;

  signin: (email: string, password: string) => Promise<void>;
  signup: (input: {
    email: string;
    password: string;
    displayName?: string;
    isUnder13: boolean;
    parentalConsentConfirmed?: boolean;
  }) => Promise<void>;
  signout: () => Promise<void>;
  logoutAll: () => Promise<void>;

  // OAuth sign-in. On native iOS / Android the caller passes a real Google /
  // Apple ID token from the SocialLogin plugin. On web we currently still pass
  // a demo idToken value of "DEMO" plus best-effort email / subject so the
  // backend's demo branch can mint a session.
  signInWithGoogle: (data: {
    idToken: string;
    email?: string;
    displayName?: string;
    subject?: string;
  }) => Promise<void>;
  signInWithApple: (data: {
    idToken: string;
    email?: string;
    displayName?: string;
    subject?: string;
  }) => Promise<void>;

  // Email verification
  verifyEmail: (token: string) => Promise<{ ok: boolean; message?: string }>;
  resendVerification: () => Promise<void>;

  // Password management
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<{ ok: boolean; message?: string }>;

  // Profile / username
  checkUsername: (username: string) => Promise<{ available: boolean; suggestion?: string; reason?: string }>;
  setUsernameAndProfile: (input: {
    username: string;
    trainerName?: string;
    friendCode?: string;
    profileScreenshotUrl?: string;
  }) => Promise<void>;

  // Trainer profile: the typed name and level (AuthModal's TrainerSetup). The
  // route still takes the stats an uploaded screenshot used to carry, from
  // store builds that predate 2026-10-02; this app sends none of them.
  confirmProfile: (data: { trainer_name: string; level: number }) => Promise<void>;

  authedFetch: (path: string, init?: RequestInit) => Promise<Response>;
  deleteAccount: () => Promise<void>;

  // v1.1.1-b1 (Fix 1 + Fix 5): inline edit of friend code or trainer
  // handle. PATCH /api/auth/me validates server-side and mirrors the
  // refreshed account row back into state.
  patchMe: (input: { friendCode?: string | null; trainerName?: string | null; trainer_level?: number | null }) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * THE TRAINER STEP IS ASKED FOR AT SIGN-UP (owner direction, 2026-10-02).
 *
 * An account that comes back from signing up or in with no trainer name is
 * asked for it there and then: two typed fields, name and level, opened by the
 * ProfileGate. Email, Google and Apple all land here, and Google / Apple cannot
 * tell a new account from a returning one on the client, so the rule is the
 * account's state rather than which button was pressed.
 *
 * Called BEFORE the account is published to state, so everything that reacts
 * to the new account (the ProfileGate, and the first-run tour, which waits for
 * this step) already sees the request on its first render.
 *
 * Restoring a session never asks: someone already in the app is not
 * interrupted, and Live Scan asks when they want to scan.
 */
function askForTrainerStep(account: PortalAccount | null | undefined): void {
  if (!account || isTrainerProfileSet(account)) return;
  requestProfileSetup(account.id);
}

/**
 * Last-known account snapshot, for INSTANT boot (2026-08-21 device report:
 * launch sat on the restoring spinner for seconds on weak signal, because the
 * app would not render until /api/auth/me came back over the network).
 *
 * Non-secret profile data only (id, email, tier, names) — the TOKEN never
 * lives here; it stays in the Keychain on native. The cache is published
 * optimistically at boot and the /api/auth/me validation still runs in the
 * background: a stale token signs the user out moments later, a fresher
 * account payload replaces the cached one when it lands.
 */
const ACCOUNT_CACHE_KEY = "nexus.account.cache.v1";

function readAccountCache(): PortalAccount | null {
  try {
    const raw = window.localStorage.getItem(ACCOUNT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PortalAccount;
    return parsed && typeof parsed.id === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Web hydrates synchronously from localStorage, exactly as before. Native
  // starts null and resolves from the Keychain in the restore effect below.
  const [token, setTokenState] = useState<string | null>(() => {
    const stored = readStoredTokenSyncWebOnly();
    // On the mirror before the first render, not after it: see publishToken.
    setAuthToken(stored);
    return stored;
  });
  const [account, setAccount] = useState<PortalAccount | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  // Tracks whether we're still validating the persisted token against the
  // backend on first load. Prevents the auth modal from flashing while we
  // restore a real session.
  //
  // On native we cannot know synchronously whether a token exists, so we must
  // assume one might and start in the restoring state. Getting this wrong is
  // not subtle: every launch would flash the sign-in screen at an already
  // signed-in user before the Keychain read lands.
  const [isRestoring, setIsRestoring] = useState<boolean>(
    () => nativePlatform() || readStoredTokenSyncWebOnly() !== null,
  );
  const restoredOnce = useRef(false);

  // The ONE place the token changes: React state and the queryClient's
  // module-level mirror (what apiRequest and the default queryFn sign with)
  // move together, synchronously.
  //
  // The mirror used to be written from an effect on [token]. That is a render
  // too late for anything below this provider: every sign-in sets the token
  // and the account in the same batch, React runs a child's effects before its
  // parent's, and so SessionProvider's per-account hydrates fired on the
  // commit that delivered the account while the mirror still held the
  // previous token. Unsigned, 401, and the saved settings were never read
  // (client/src/test/sessionHydrateAuth.test.tsx). Nothing else may call
  // setTokenState.
  const publishToken = useCallback((next: string | null) => {
    setAuthToken(next);
    setTokenState(next);
  }, []);

  // setToken wrapper that persists every change. The write is intentionally
  // fire-and-forget: persistToken never throws, and awaiting a Keychain round
  // trip inside a synchronous setter would make every caller async for no
  // user-visible benefit.
  const setToken = useCallback((next: string | null) => {
    publishToken(next);
    void persistToken(next);
  }, [publishToken]);

  // Refetch any queries that 401’d for want of a token before it arrived.
  useEffect(() => {
    if (token) queryClient.invalidateQueries();
  }, [token]);

  // Workstream C: tag Sentry events with the numeric account id only. No
  // email, username, or trainer name ever leaves the device. See
  // client/src/lib/sentry.ts for the gating contract.
  useEffect(() => {
    setSentryAccount(account?.id ?? null);
  }, [account?.id]);

  // Keep the boot cache in step with the live account. The null case is
  // deliberate about WHY it is null: token also gone means a real sign-out
  // (or a stale token cleared), so the cache goes too; account null while a
  // token remains is a restore in flight or an offline launch — exactly when
  // the cache must survive.
  //
  // And not while the restore is still running (2026-10-06): native boots
  // with token AND account null, so this effect's first run removed the
  // cache before loadToken had resolved, and the fast path below never had
  // anything to show. On a cold start whose /me reply then failed, the
  // trainer saw the sign-in screen with a perfectly good session in storage.
  useEffect(() => {
    try {
      if (account) {
        window.localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(account));
      } else if (!token && !isRestoring) {
        window.localStorage.removeItem(ACCOUNT_CACHE_KEY);
      }
    } catch {
      /* quota / private-mode: boot just stays network-bound */
    }
  }, [account, token, isRestoring]);

  // On first mount, if we hydrated a token from storage, validate it against
  // the backend. If it's still good we restore the account; if it's expired
  // or the user signed out elsewhere we silently clear.
  useEffect(() => {
    if (restoredOnce.current) return;
    restoredOnce.current = true;
    (async () => {
      // On native this is the Keychain read (and the one-time migration of a
      // legacy localStorage token). On web it re-reads the same value the
      // useState initializer already used.
      const stored = await loadToken();
      if (!stored) {
        setIsRestoring(false);
        return;
      }
      // Native booted with a null token, so publish what we just loaded before
      // validating it — otherwise authedFetch and the queryClient mirror would
      // keep sending unauthenticated requests for the rest of this session.
      // Not setToken: this value came FROM storage, there is nothing to persist.
      publishToken(stored);
      // FAST PATH: a returning user renders the app NOW from the cached
      // account instead of staring at the restoring spinner for a network
      // round-trip. The /api/auth/me below still runs: success refreshes the
      // account, a 401 signs out (the optimistic frame lasts only as long as
      // the request), and a network failure leaves the cached session
      // standing — which is what offline launch should do.
      const cached = readAccountCache();
      if (cached) {
        setAccount(cached);
        setIsRestoring(false);
      }
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAccount(data.account);
          // Token is already in state: web from the useState initializer,
          // native from the publishToken above.
        } else if (res.status === 401) {
          // The server says this session is gone (revoked, expired, account
          // deleted): clear so the user sees the sign-in screen.
          console.log("[auth] restore: session rejected", { status: res.status });
          setToken(null);
          setAccount(null);
        } else {
          // Any other failure (an edge 5xx, a 429, a deploy in flight) says
          // nothing about the session, so it is treated like a network error:
          // the token and the cached account stand. A tester's Galaxy was
          // signed out on a cold start on 2026-10-06 while its server session
          // was intact and no logout was ever sent; this branch was the only
          // path left, and it used to sign out on every non-2xx.
          console.log("[auth] restore: /me failed, session kept", { status: res.status });
        }
      } catch {
        // Network error: keep the token, the user can retry. Account stays null
        // so guarded routes show their loading state until next request.
        console.log("[auth] restore: /me unreachable, session kept");
      } finally {
        setIsRestoring(false);
      }
    })();
  }, [setToken, publishToken]);

  const openAuth = useCallback((mode: "signin" | "signup" = "signin") => {
    setAuthMode(mode);
    setIsAuthOpen(true);
  }, []);
  const closeAuth = useCallback(() => setIsAuthOpen(false), []);

  const authedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      if (init?.body) headers.set("Content-Type", "application/json");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
      return res;
    },
    [token],
  );

  const signin = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Sign in failed");
    }
    const data = await res.json();
    askForTrainerStep(data.account);
    setToken(data.session.token);
    setAccount(data.account);
    setIsAuthOpen(false);
  }, []);

  const signup = useCallback(
    async (input: {
      email: string;
      password: string;
      displayName?: string;
      isUnder13: boolean;
      parentalConsentConfirmed?: boolean;
    }) => {
      // Attach the first touch captured at landing (migration 0073). Read at
      // send time rather than held in state, because signup can happen many
      // navigations after the click that brought them here.
      const touch = readFirstTouch();
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...input,
          ...(touch
            ? {
                acqSource: touch.source ?? undefined,
                acqMedium: touch.medium ?? undefined,
                acqCampaign: touch.campaign ?? undefined,
                acqReferrer: touch.referrer ?? undefined,
                acqFirstSeenAt: touch.firstSeenAt,
              }
            : {}),
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Sign up failed");
      }
      const data = await res.json();
      askForTrainerStep(data.account);
      setToken(data.session.token);
      setAccount(data.account);
      // Not closed here: AuthModal closes itself once signup() resolves.
    },
    [],
  );

  const signout = useCallback(async () => {
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // ignore network errors on sign out
      }
    }
    setToken(null);
    setAccount(null);
  }, [token]);

  const logoutAll = useCallback(async () => {
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout-all`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // ignore network errors
      }
    }
    setToken(null);
    setAccount(null);
  }, [token]);

  // ── OAuth (demo mode) ─────────────────────────────────────────────────────

  const signInWithGoogle = useCallback(
    async (data: {
      idToken: string;
      email?: string;
      displayName?: string;
      subject?: string;
    }) => {
      const res = await fetch(`${API_BASE}/api/auth/oauth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Google sign-in failed");
      }
      const body = await res.json();
      askForTrainerStep(body.account);
      setToken(body.session.token);
      setAccount(body.account);
      setIsAuthOpen(false);
    },
    [],
  );

  const signInWithApple = useCallback(
    async (data: {
      idToken: string;
      email?: string;
      displayName?: string;
      subject?: string;
    }) => {
      const res = await fetch(`${API_BASE}/api/auth/oauth/apple`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Apple sign-in failed");
      }
      const body = await res.json();
      askForTrainerStep(body.account);
      setToken(body.session.token);
      setAccount(body.account);
      setIsAuthOpen(false);
    },
    [],
  );

  // ── Email verification ────────────────────────────────────────────────────

  const verifyEmail = useCallback(async (verifyToken: string): Promise<{ ok: boolean; message?: string }> => {
    const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: verifyToken }),
    });
    const data = await res.json();
    if (data.ok && account) {
      setAccount({ ...account, emailVerified: true });
    }
    return data;
  }, [account]);

  const resendVerification = useCallback(async () => {
    const res = await authedFetch("/api/auth/resend-verification", { method: "POST", body: "{}" });
    if (!res.ok) throw new Error(await res.text());
  }, [authedFetch]);

  // ── Password management ───────────────────────────────────────────────────

  const forgotPassword = useCallback(async (email: string) => {
    await fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    // Always resolves - server returns ok:true regardless of whether email exists.
  }, []);

  const resetPassword = useCallback(
    async (resetToken: string, newPassword: string): Promise<{ ok: boolean; message?: string }> => {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      return res.json();
    },
    [],
  );

  // ── Profile / username ────────────────────────────────────────────────────

  const checkUsername = useCallback(
    async (username: string): Promise<{ available: boolean; suggestion?: string; reason?: string }> => {
      const res = await fetch(`${API_BASE}/api/auth/check-username`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      return res.json();
    },
    [],
  );

  const setUsernameAndProfile = useCallback(
    async (input: {
      username: string;
      trainerName?: string;
      friendCode?: string;
      profileScreenshotUrl?: string;
    }) => {
      const res = await authedFetch("/api/auth/set-username", {
        method: "POST",
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Could not set username");
      }
      const data = await res.json();
      setAccount(data.account);
    },
    [authedFetch],
  );

  const confirmProfile = useCallback(
    async (data: { trainer_name: string; level: number }) => {
      const res = await authedFetch("/api/auth/portal/profile/confirm", {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Could not save profile");
      }
      const result = await res.json();
      setAccount(result.account);
      // The trainer's profile row (level, shown under Settings > Trainer
      // stats) is cached with staleTime Infinity and was fetched at sign-in,
      // before this save created or changed it.
      queryClient.invalidateQueries({ queryKey: ["/api/settings/all"] });
    },
    [authedFetch],
  );

  const deleteAccount = useCallback(async () => {
    const res = await authedFetch("/api/account", { method: "DELETE" });
    if (!res.ok) throw new Error(await res.text());
    setToken(null);
    setAccount(null);
  }, [authedFetch]);

  // v1.1.1-b1 (Fix 1 + Fix 5): inline edit of friend code + trainer
  // handle. Server validates and returns the refreshed account row
  // which we mirror into state.
  const patchMe = useCallback(
    async (input: { friendCode?: string | null; trainerName?: string | null; trainer_level?: number | null }) => {
      const res = await authedFetch("/api/auth/me", {
        method: "PATCH",
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Could not save profile");
      }
      const data = await res.json();
      setAccount(data.account);
    },
    [authedFetch],
  );

  const value = useMemo<AuthState>(
    () => ({
      token,
      account,
      isSignedIn: !!token && !!account,
      isRestoring,
      isAuthOpen,
      authMode,
      openAuth,
      closeAuth,
      signin,
      signup,
      signout,
      logoutAll,
      signInWithGoogle,
      signInWithApple,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
      checkUsername,
      setUsernameAndProfile,
      confirmProfile,
      authedFetch,
      deleteAccount,
      patchMe,
    }),
    [
      token,
      account,
      isRestoring,
      isAuthOpen,
      authMode,
      openAuth,
      closeAuth,
      signin,
      signup,
      signout,
      logoutAll,
      signInWithGoogle,
      signInWithApple,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
      checkUsername,
      setUsernameAndProfile,
      confirmProfile,
      authedFetch,
      deleteAccount,
      patchMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * Auth state when it happens to be available, null otherwise.
 *
 * For components that only ENRICH themselves with auth — an admin-gated
 * diagnostic, say — rather than requiring it. Using useAuth for those forces
 * every ancestor to sit under AuthProvider or crash, which quietly turns an
 * optional garnish into a hard dependency of whatever page mounts it. Callers
 * must treat null as "not signed in", which for a gated feature means hidden.
 */
export function useAuthOptional(): AuthState | null {
  return useContext(AuthContext);
}
